import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getInitials, getRandomColor } from '../../utils/helpers';
import { useModal } from '../../context/ModalContext';
import {
  exportToCSV,
  exportToJSON,
  exportToExcel,
  exportToPDF,
  formatForExport,
  getExportFilename
} from '../../utils/exportUtils';
import {
  getBirthdayMembers,
  getUpcomingBirthdays,
  getBirthdayStats,
  getBirthdaySettings,
  updateBirthdaySettings,
  sendBirthdayMessage,
  sendBulkBirthdayMessages,
  exportBirthdayData,
  updateMemberBirthday,
  clearApiMessage,
  clearError,
  clearBirthdayState,
  selectBirthdayMembers,
  selectBirthdayUpcoming,
  selectBirthdayStats,
  selectBirthdaySettings,
  selectBirthdayPagination,
  selectBirthdayLoading,
  selectBirthdayError,
  selectBirthdayApiMessage,
  selectBirthdaySuccess,
} from '../../store/slices/engagement/birthdaySlice';
import { selectCurrentOrganization } from '../../store/slices/authSlice';
import { getMergeFieldValue } from '../../utils/mergeFieldValues';
import {
  Download, BarChart3, ClipboardList, TrendingUp, FileText, Settings, Lightbulb,
  Save, Pencil, Cake, Users, Search, CheckCircle2, XCircle, Send, Calendar,
  PartyPopper, AlertTriangle, NotebookPen, Loader2, Mail, Send as SendIcon
} from 'lucide-react';

const Birthdays = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();

  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [localMessageTemplate, setLocalMessageTemplate] = useState('');
  const organization = useSelector(selectCurrentOrganization);
  const members = useSelector(selectBirthdayMembers) || [];

  const mergeFields = [
    { key: '{FirstName}', label: 'First Name', description: "Member's first name", example: 'John' },
    { key: '{LastName}', label: 'Last Name', description: "Member's last name", example: 'Doe' },
    { key: '{FullName}', label: 'Full Name', description: "Member's full name", example: 'John Doe' },
    { key: '{ChurchName}', label: 'Church Name', description: 'Your church name', example: 'Grace Chapel' },
    { key: '{PastorName}', label: 'Pastor Name', description: "Pastor's name", example: 'Pastor Michael' },
    { key: '{Group}', label: 'Group', description: "Member's group", example: 'Youth Ministry' },
    { key: '{Branch}', label: 'Branch', description: "Member's branch", example: 'Main Campus' },
    { key: '{Age}', label: 'Age', description: "Member's age", example: '25' },
    { key: '{BirthdayDate}', label: 'Birthday Date', description: "Member's birthday date", example: '15 May' },
    { key: '{TodayDate}', label: 'Today\'s Date', description: 'Current date', example: '20 May 2024' },
  ].map((field) => ({ ...field, example: getMergeFieldValue(field.key, { member: members[0], organization }) }));

  // ==================== REFS ====================
  const editMessageRef = useRef(null);
  const editSendTimeRef = useRef(null);
  const editNotifyPastorRef = useRef(null);
  const editAnniversaryRef = useRef(null);
  const memberDobRef = useRef(null);
  const memberSendBirthdayRef = useRef(null);
  const memberSendAnniversaryRef = useRef(null);
  const memberNotesRef = useRef(null);
  const templateTimeoutRef = useRef(null);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const upcoming = useSelector(selectBirthdayUpcoming) || [];
  const stats = useSelector(selectBirthdayStats) || {};
  const settings = useSelector(selectBirthdaySettings) || {
    send_time: '08:00',
    message_template: 'Happy Birthday, {FirstName}! 🎉 We celebrate you today and thank God for your life. May this new year overflow with His blessings, good health, and abundant joy. You are dearly loved by the {ChurchName} family! — {PastorName}',
    notify_pastor: true,
    send_anniversary: true,
    is_active: true
  };
  const pagination = useSelector(selectBirthdayPagination);
  const loading = useSelector(selectBirthdayLoading);
  const apiMessage = useSelector(selectBirthdayApiMessage);
  const error = useSelector(selectBirthdayError);
  const successMessage = useSelector(selectBirthdaySuccess);

  // ==================== HELPER: FORMAT DATE ====================
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short'
    });
  };

  // ==================== EFFECTS ====================

  // Update local state when settings change from Redux
  useEffect(() => {
    setLocalMessageTemplate(settings.message_template || '');
  }, [settings.message_template]);

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(getBirthdayMembers({
          per_page: itemsPerPage,
          page: currentPage,
          search: searchTerm
        }));
        await dispatch(getUpcomingBirthdays({ days: 30, limit: 50 }));
        await dispatch(getBirthdayStats());
        await dispatch(getBirthdaySettings());
      } catch (err) {
        console.error('Error loading birthday data:', err);
      }
    };

    loadData();

    return () => {
      dispatch(clearBirthdayState());
      if (templateTimeoutRef.current) {
        clearTimeout(templateTimeoutRef.current);
      }
    };
  }, [dispatch]);

  // Handle filters and pagination
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm
      };

      dispatch(getBirthdayMembers(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, currentPage, itemsPerPage, dispatch]);

  // Display API messages - with deduplication
  useEffect(() => {
    const message = apiMessage || successMessage;
    if (!message || !showToast) return;

    const messageKey = `${message}_${error ? 'error' : 'success'}`;
    const now = Date.now();

    if (lastToastRef.current.message === messageKey &&
      (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }

    lastToastRef.current = { message: messageKey, timestamp: now };

    const toastType = error ? 'error' : 'success';
    showToast(message, toastType);

    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, successMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== HELPERS ====================

  const insertField = (textareaRef, field) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const newValue = value.substring(0, start) + field + value.substring(end);
    textarea.value = newValue;
    textarea.focus();
    textarea.selectionStart = textarea.selectionEnd = start + field.length;

    // Update local state
    setLocalMessageTemplate(newValue);

    // Trigger React state update
    const event = new Event('input', { bubbles: true });
    textarea.dispatchEvent(event);

    // Debounced Redux update
    if (templateTimeoutRef.current) {
      clearTimeout(templateTimeoutRef.current);
    }
    templateTimeoutRef.current = setTimeout(() => {
      dispatch(updateBirthdaySettings({ message_template: newValue }));
    }, 500);
  };

  // ==================== PAGINATION ====================
  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.last_page || 1;
  const currentPageNum = pagination?.current_page || 1;
  const perPage = pagination?.per_page || 15;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNum > 1) {
      setCurrentPage(currentPageNum - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPageNum < totalPages) {
      setCurrentPage(currentPageNum + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== EXPORT FUNCTIONS ====================

  const handleExport = async (type, birthdayId = null, format = 'csv') => {
    try {
      setIsSubmitting(true);
      const params = {};
      if (birthdayId) params.birthday_id = birthdayId;

      const result = await dispatch(exportBirthdayData({ type, params })).unwrap();

      if (result.success && result.data) {
        let data = result.data;

        if (data.data && Array.isArray(data.data)) {
          data = data.data;
        }
        else if (typeof data === 'object' && !Array.isArray(data)) {
          if (data.data && Array.isArray(data.data)) {
            data = data.data;
          } else {
            data = Object.values(data);
          }
        }

        if (!Array.isArray(data) || data.length === 0) {
          showToastMessage('No data to export', 'error');
          return;
        }

        const filename = getExportFilename(`birthdays-${type}`);

        switch (format.toLowerCase()) {
          case 'csv':
            exportToCSV(data, filename);
            break;
          case 'json':
            exportToJSON(data, filename);
            break;
          case 'excel':
            exportToExcel(data, filename);
            break;
          case 'pdf':
            exportToPDF(data, 'Birthdays Report', filename);
            break;
          default:
            showToastMessage('Unsupported export format', 'error');
            return;
        }

        showToastMessage(`📥 ${data.length} records exported as ${format.toUpperCase()}!`, 'success');
        closeModal();
      }
    } catch (err) {
      console.error('Export error:', err);
      showToastMessage(err.message || 'Failed to export data', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Export Modal
  const openExportModal = (exportType = 'upcoming') => {
    let data = [];
    let title = 'Birthdays Report';
    let filename = 'birthdays-report';

    if (exportType === 'upcoming') {
      data = upcoming.map(m => ({
        'Name': m.name,
        'Birthday': m.dob,
        'Group': m.group,
        'Days Until': m.days === 0 ? 'Today!' : m.days === '-' ? 'Passed' : `${m.days} days`,
        'Status': m.days === 0 ? 'Today' : m.days === '-' ? 'Passed' : 'Upcoming'
      }));
      title = 'Upcoming Birthdays';
      filename = 'upcoming-birthdays';
    } else if (exportType === 'all-members') {
      data = members.map(m => {
        const dob = m.birthday?.dob || m.dob;
        let formatted = 'N/A';
        if (dob) {
          const date = new Date(dob);
          if (!isNaN(date.getTime())) {
            formatted = formatDate(dob);
          }
        }
        return {
          'Name': m.name,
          'Birthday': formatted,
          'Group': m.group || 'N/A',
          'Branch': m.branch || 'N/A',
          'Status': m.status || 'Active',
          'Send Birthday': m.birthday?.send_birthday !== false ? 'Yes' : 'No',
          'Email': m.email || 'N/A'
        };
      });
      title = 'All Members Birthday List';
      filename = 'all-members-birthdays';
    } else if (exportType === 'settings') {
      data = [
        { 'Setting': 'Send Time', 'Value': settings.send_time || '08:00' },
        { 'Setting': 'Notify Pastor', 'Value': settings.notify_pastor !== false ? 'Yes' : 'No' },
        { 'Setting': 'Send Anniversary', 'Value': settings.send_anniversary !== false ? 'Yes' : 'No' },
        { 'Setting': 'Message Template', 'Value': settings.message_template || '' }
      ];
      title = 'Birthday Settings';
      filename = 'birthday-settings';
    } else {
      // fallback to upcoming
      data = upcoming.map(m => ({
        'Name': m.name,
        'Birthday': m.dob,
        'Group': m.group,
        'Days Until': m.days === 0 ? 'Today!' : m.days === '-' ? 'Passed' : `${m.days} days`,
        'Status': m.days === 0 ? 'Today' : m.days === '-' ? 'Passed' : 'Upcoming'
      }));
      title = 'Birthdays Report';
      filename = 'birthdays';
    }

    if (data.length === 0) {
      showToastMessage('No data to export', 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Export {title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {data.length} record{data.length !== 1 ? 's' : ''} in your preferred format.
          </div>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleExport(exportType, null, format)}
                  disabled={isSubmitting}
                  style={{
                    padding: '8px',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span style={{ display: 'flex' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <ClipboardList size={20} />}
                    {format === 'Excel' && <TrendingUp size={20} />}
                    {format === 'PDF' && <FileText size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>
          <div style={{ padding: '10px 12px', background: 'var(--cream)', borderRadius: '8px', fontSize: '12px', color: 'var(--text-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><BarChart3 size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{data.length} records will be exported</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // ==================== BULK SEND MODAL ====================

  const handleBulkSend = () => {
    const membersWithBirthday = members.filter(m => m.birthday?.send_birthday !== false);
    if (membersWithBirthday.length === 0) {
      showToastMessage('No members with birthday sending enabled.', 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><SendIcon size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Send Bulk Birthday Messages</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>
              Send birthday messages to <strong>{membersWithBirthday.length}</strong> members with sending enabled.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Custom Message (optional)</label>
            <textarea
              className="form-textarea"
              rows="4"
              ref={editMessageRef}
              defaultValue={settings.message_template || ''}
              placeholder="Leave blank to use the default template"
            />
            <div className="form-hint">Merge fields: {'{FirstName}'}, {'{LastName}'}, {'{FullName}'}, {'{ChurchName}'}, {'{PastorName}'}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)' }}>Send email also (if available)</div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Will send email in addition to SMS</div>
            </div>
            <label className="toggle">
              <input type="checkbox" ref={editNotifyPastorRef} defaultChecked={false} />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            disabled={isSubmitting}
            onClick={async () => {
              const customMessage = editMessageRef.current?.value?.trim() || null;
              const sendEmail = editNotifyPastorRef.current?.checked || false;

              setIsSubmitting(true);
              try {
                const memberIds = membersWithBirthday.map(m => m.id);
                const result = await dispatch(sendBulkBirthdayMessages({
                  member_ids: memberIds,
                  message: customMessage,
                  send_email: sendEmail
                })).unwrap();
                closeModal();
                showToastMessage(`📨 Bulk messages processed: ${result.sent} sent, ${result.failed} failed`, 'success');
                await dispatch(getBirthdayStats());
              } catch (err) {
                showToastMessage(err.message || 'Failed to send bulk messages', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Sending...' : 'Send Bulk Messages'}
          </button>
        </div>
      </div>
    );
  };

  // ==================== SETTINGS HANDLERS ====================

  const handleSaveSettings = async () => {
    try {
      setIsSubmitting(true);
      await dispatch(updateBirthdaySettings({
        send_time: settings.send_time,
        message_template: localMessageTemplate,
        notify_pastor: settings.notify_pastor,
        send_anniversary: settings.send_anniversary,
        is_active: true
      })).unwrap();
      showToastMessage('💾 Birthday settings saved!', 'success');
    } catch (err) {
      showToastMessage(err.message || 'Failed to save settings', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit Settings Modal
  const handleEditSettings = () => {
    setTimeout(() => {
      if (editMessageRef.current) editMessageRef.current.value = settings.message_template || '';
      if (editSendTimeRef.current) editSendTimeRef.current.value = settings.send_time || '08:00';
      if (editNotifyPastorRef.current) editNotifyPastorRef.current.checked = settings.notify_pastor !== false;
      if (editAnniversaryRef.current) editAnniversaryRef.current.checked = settings.send_anniversary !== false;
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Settings size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Edit Birthday Settings</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Send Time</label>
            <input
              className="form-input"
              type="time"
              ref={editSendTimeRef}
              defaultValue={settings.send_time || '08:00'}
            />
            <div className="form-hint">Time of day to send birthday messages</div>
          </div>
          <div className="form-group">
            <label className="form-label">Birthday Message Template <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea
              className="form-textarea"
              rows="4"
              ref={editMessageRef}
              defaultValue={settings.message_template || ''}
              placeholder="Enter your birthday message template..."
            />
            <div className="form-hint">
              Use {'{FirstName}'}, {'{LastName}'}, {'{FullName}'}, {'{ChurchName}'}, {'{PastorName}'} for personalisation
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Available Merge Fields</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {mergeFields.map(field => (
                <button
                  key={field.key}
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const textarea = editMessageRef.current;
                    if (textarea) {
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const value = textarea.value;
                      const newValue = value.substring(0, start) + field.key + value.substring(end);
                      textarea.value = newValue;
                      textarea.focus();
                      textarea.selectionStart = textarea.selectionEnd = start + field.key.length;
                    }
                  }}
                  title={`${field.label}: ${field.description} (e.g. ${field.example})`}
                >
                  {field.key}
                </button>
              ))}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
              <Lightbulb size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Click a merge field to insert it at the cursor position
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '10px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)' }}>Notify pastor before sending</div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Admin gets an SMS alert the day before</div>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                ref={editNotifyPastorRef}
                defaultChecked={settings.notify_pastor !== false}
                onChange={(e) => {
                  dispatch(updateBirthdaySettings({ notify_pastor: e.target.checked }));
                }}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)' }}>Anniversary messages</div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Also send on membership join anniversaries</div>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                ref={editAnniversaryRef}
                defaultChecked={settings.send_anniversary !== false}
                onChange={(e) => {
                  dispatch(updateBirthdaySettings({ send_anniversary: e.target.checked }));
                }}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          <div style={{
            marginTop: '12px',
            padding: '10px 12px',
            background: 'var(--blue-bg)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--blue)'
          }}>
            <Lightbulb size={14} style={{ verticalAlign: '-3px', marginRight: '6px' }} />
            Sender ID is automatically managed by the system. No manual configuration needed.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            disabled={isSubmitting}
            onClick={async () => {
              const newMessage = editMessageRef.current?.value?.trim() || '';

              if (!newMessage) {
                showToastMessage('Please enter a message template', 'error');
                return;
              }

              const updates = {
                message_template: newMessage,
                send_time: editSendTimeRef.current?.value || settings.send_time,
                notify_pastor: editNotifyPastorRef.current?.checked !== false,
                send_anniversary: editAnniversaryRef.current?.checked !== false,
              };

              try {
                setIsSubmitting(true);
                await dispatch(updateBirthdaySettings(updates)).unwrap();
                showToastMessage('✅ Birthday settings updated!', 'success');
                closeModal();
                await dispatch(getBirthdaySettings());
              } catch (err) {
                showToastMessage(err.message || 'Failed to update settings', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting
              ? <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Saving...</>
              : <><Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Changes</>}
          </button>
        </div>
      </div>
    );
  };

  // ==================== MEMBER HANDLERS ====================

  const handleSendNow = async (member) => {
    try {
      setIsSubmitting(true);
      const result = await dispatch(sendBirthdayMessage({
        memberId: member.id,
        data: { send_now: true }
      })).unwrap();

      if (result.success) {
        showToastMessage(`📨 Birthday SMS sent to ${member.name}!`, 'success');
        await dispatch(getBirthdayStats());
      }
    } catch (err) {
      showToastMessage(err.message || `Failed to send SMS to ${member.name}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditMember = (member) => {
    setEditingMemberId(member.id);

    setTimeout(() => {
      if (memberDobRef.current) {
        const dob = member.birthday?.dob || member.dob;
        if (dob) {
          const date = new Date(dob);
          if (!isNaN(date.getTime())) {
            memberDobRef.current.value = date.toISOString().split('T')[0];
          }
        }
      }
      if (memberSendBirthdayRef.current) {
        memberSendBirthdayRef.current.checked = member.birthday?.send_birthday !== false;
      }
      if (memberSendAnniversaryRef.current) {
        memberSendAnniversaryRef.current.checked = member.birthday?.send_anniversary !== false;
      }
      if (memberNotesRef.current) {
        memberNotesRef.current.value = member.birthday?.notes || '';
      }
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Edit Birthday: {member.name}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Date of Birth</label>
            <input
              className="form-input"
              type="date"
              ref={memberDobRef}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '10px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)' }}>Send Birthday Message</div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Automatically send on their birthday</div>
            </div>
            <label className="toggle">
              <input type="checkbox" ref={memberSendBirthdayRef} defaultChecked />
              <span className="toggle-slider"></span>
            </label>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '10px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)' }}>Send Anniversary Message</div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Send on membership anniversary</div>
            </div>
            <label className="toggle">
              <input type="checkbox" ref={memberSendAnniversaryRef} defaultChecked />
              <span className="toggle-slider"></span>
            </label>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-textarea"
              rows="3"
              ref={memberNotesRef}
              placeholder="Additional notes about this member..."
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            disabled={isSubmitting}
            onClick={async () => {
              const data = {
                dob: memberDobRef.current?.value || null,
                send_birthday: memberSendBirthdayRef.current?.checked !== false,
                send_anniversary: memberSendAnniversaryRef.current?.checked !== false,
                notes: memberNotesRef.current?.value || null
              };

              try {
                setIsSubmitting(true);
                await dispatch(updateMemberBirthday({
                  memberId: member.id,
                  data
                })).unwrap();
                showToastMessage(`✅ ${member.name}'s birthday updated!`, 'success');
                closeModal();
                await dispatch(getBirthdayMembers({
                  per_page: itemsPerPage,
                  page: currentPage,
                  search: searchTerm
                }));
                await dispatch(getUpcomingBirthdays({ days: 30, limit: 50 }));
              } catch (err) {
                showToastMessage(err.message || 'Failed to update member', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting
              ? <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Saving...</>
              : <><Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Changes</>}
          </button>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================

  const statsData = stats || {
    total_members: 0,
    with_birthday: 0,
    without_birthday: 0,
    messages_sent_this_year: 0,
    upcoming_in_7_days: 0,
    stats: {
      avg_response_rate: 0
    }
  };

  return (
    <div className="grid-65-35">
      <div>
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header">
            <div>
              <div className="card-title"><Cake size={16} style={{ verticalAlign: '-3px' }} /> Birthday Automation Settings</div>
              <div className="card-sub">Configure how birthday messages are sent</div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span className={settings.is_active !== false ? 'badge badge-green' : 'badge badge-red'}>
                {settings.is_active !== false ? 'Active' : 'Inactive'}
              </span>
              <button
                className="btn btn-ghost btn-sm btn-icon"
                onClick={() => openExportModal('settings')}
                title="Export settings"
                disabled={isSubmitting}
              >
                <Download size={14} />
              </button>
              <button
                className="btn btn-ghost btn-sm btn-icon"
                onClick={handleEditSettings}
                title="Edit birthday settings"
                disabled={isSubmitting}
              >
                <Pencil size={14} />
              </button>
            </div>
          </div>

          <div className="form-row" style={{ marginBottom: '14px' }}>
            <div className="form-group">
              <label className="form-label">Send Time</label>
              <input
                className="form-input"
                type="time"
                value={settings.send_time || '08:00'}
                onChange={(e) => {
                  dispatch(updateBirthdaySettings({ send_time: e.target.value }));
                }}
                title="Time of day to send birthday messages"
                disabled={loading}
              />
              <div className="form-hint">Time of day when birthday messages will be sent</div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Birthday Message Template</label>
            <textarea
              className="form-textarea birthday-template-textarea"
              rows="4"
              value={localMessageTemplate}
              onChange={(e) => {
                const newValue = e.target.value;
                setLocalMessageTemplate(newValue);
                if (templateTimeoutRef.current) {
                  clearTimeout(templateTimeoutRef.current);
                }
                templateTimeoutRef.current = setTimeout(() => {
                  dispatch(updateBirthdaySettings({ message_template: newValue }));
                }, 500);
              }}
              title="Birthday message template with merge fields"
              placeholder="Enter your birthday message template..."
              disabled={loading}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
              {mergeFields.map(field => (
                <button
                  key={field.key}
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const textarea = document.querySelector('.birthday-template-textarea');
                    if (textarea) {
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const currentValue = textarea.value;
                      const newValue = currentValue.substring(0, start) + field.key + currentValue.substring(end);
                      textarea.value = newValue;
                      setLocalMessageTemplate(newValue);
                      textarea.focus();
                      textarea.selectionStart = textarea.selectionEnd = start + field.key.length;
                      if (templateTimeoutRef.current) {
                        clearTimeout(templateTimeoutRef.current);
                      }
                      templateTimeoutRef.current = setTimeout(() => {
                        dispatch(updateBirthdaySettings({ message_template: newValue }));
                      }, 500);
                    }
                  }}
                  title={`${field.label}: ${field.description} (e.g. ${field.example})`}
                >
                  {field.key}
                </button>
              ))}
            </div>
            <div className="char-count" style={{ fontSize: '11px', color: 'var(--text-light)', textAlign: 'right', marginTop: '4px' }}>
              <NotebookPen size={11} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{localMessageTemplate.length} characters · {Math.ceil(localMessageTemplate.length / 160)} SMS
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '14px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)' }}>Notify pastor before sending</div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Admin gets an SMS alert the day before</div>
            </div>
            <label className="toggle" title={settings.notify_pastor !== false ? 'Click to disable' : 'Click to enable'}>
              <input
                type="checkbox"
                checked={settings.notify_pastor !== false}
                onChange={(e) => {
                  dispatch(updateBirthdaySettings({ notify_pastor: e.target.checked }));
                }}
                disabled={loading}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '14px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)' }}>Anniversary messages</div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Also send on membership join anniversaries</div>
            </div>
            <label className="toggle" title={settings.send_anniversary !== false ? 'Click to disable' : 'Click to enable'}>
              <input
                type="checkbox"
                checked={settings.send_anniversary !== false}
                onChange={(e) => {
                  dispatch(updateBirthdaySettings({ send_anniversary: e.target.checked }));
                }}
                disabled={loading}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div style={{
            marginTop: '12px',
            padding: '10px 12px',
            background: 'var(--blue-bg)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--blue)'
          }}>
            <Lightbulb size={14} style={{ verticalAlign: '-3px', marginRight: '6px' }} />
            Sender ID is automatically managed by the system. No manual configuration needed.
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
            <button
              className="btn btn-gold btn-sm"
              onClick={handleSaveSettings}
              title="Save birthday settings"
              disabled={isSubmitting || loading}
            >
              {isSubmitting
                ? <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Saving...</>
                : <><Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Settings</>}
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleEditSettings}
              title="Edit settings in modal"
              disabled={loading}
            >
              <Settings size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Advanced Edit
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => openExportModal('settings')}
              title="Export settings"
              disabled={isSubmitting}
            >
              <Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export Settings
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title"><Users size={16} style={{ verticalAlign: '-3px' }} /> All Member Birthdays</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                className="btn btn-gold btn-sm"
                onClick={handleBulkSend}
                title="Send bulk birthday messages to all members with sending enabled"
                disabled={loading || isSubmitting}
              >
                <SendIcon size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} /> Bulk Send
              </button>
              <div className="search-box" style={{ maxWidth: '200px' }}>
                <span className="search-icon"><Search size={16} /></span>
                <input
                  type="text"
                  placeholder="Search members…"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => openExportModal('all-members')}
                title="Export all members with birthdays"
                disabled={isSubmitting}
              >
                <Download size={14} />
              </button>
            </div>
          </div>

          <div className="table-wrap">
            {loading && members.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><Loader2 size={20} /></div>
                <div style={{ color: 'var(--text-light)' }}>Loading members...</div>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Birthday</th>
                    <th>Group</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {members.length > 0 ? (
                    members.map(m => {
                      const color = getRandomColor(m.id);
                      const dob = m.birthday?.dob || m.dob;
                      let formatted = 'N/A';
                      if (dob) {
                        const date = new Date(dob);
                        if (!isNaN(date.getTime())) {
                          formatted = formatDate(dob);
                        }
                      }
                      return (
                        <tr key={m.id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div className="member-row-avatar" style={{ background: color, width: '28px', height: '28px', fontSize: '11px' }}>
                                {getInitials(m.full_name || `${m.first_name || ''} ${m.last_name || ''}`.trim())}
                              </div>
                              <span style={{ fontWeight: '500' }}>
                                {m.full_name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || 'Unnamed member'}
                              </span>
                            </div>
                          </td>
                          <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }}>{formatted}</td>
                          <td><span className="badge badge-navy" style={{ fontSize: '10px' }}>{m.group || 'General'}</span></td>
                          <td>
                            <span className={m.birthday?.send_birthday !== false ? 'badge badge-green' : 'badge badge-red'} style={{ fontSize: '10px' }}>
                              {m.birthday?.send_birthday !== false
                                ? <><CheckCircle2 size={11} style={{ verticalAlign: '-2px' }} /> Enabled</>
                                : <><XCircle size={11} style={{ verticalAlign: '-2px' }} /> Disabled</>}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                className="btn btn-ghost btn-sm btn-icon"
                                onClick={() => handleSendNow(m)}
                                title={`Send birthday SMS to ${m.name}`}
                                disabled={isSubmitting || loading}
                              >
                                <Send size={14} />
                              </button>
                              <button
                                className="btn btn-ghost btn-sm btn-icon"
                                onClick={() => handleEditMember(m)}
                                title={`Edit ${m.name}'s birthday`}
                                disabled={loading}
                              >
                                <Pencil size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5">
                        <div className="empty-state">
                          <div className="empty-icon"><Search size={32} strokeWidth={1.5} /></div>
                          <div className="empty-title">No members found</div>
                          <div className="empty-text">Try adjusting your search</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px',
              paddingTop: '14px',
              borderTop: '1px solid var(--border)',
              marginTop: '4px'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                Showing <strong>{((currentPageNum - 1) * perPage) + 1}</strong> to <strong>{Math.min(currentPageNum * perPage, totalItems)}</strong> of <strong>{totalItems}</strong> members
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                  <select
                    className="form-select"
                    style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    title="Items per page"
                    disabled={loading}
                  >
                    <option value={3}>3</option>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={goToPreviousPage}
                    disabled={currentPageNum === 1 || loading}
                    style={{
                      opacity: currentPageNum === 1 ? 0.5 : 1,
                      cursor: currentPageNum === 1 ? 'not-allowed' : 'pointer'
                    }}
                    title="Previous page"
                  >
                    ← Prev
                  </button>

                  {getPageNumbers().map(page => (
                    <button
                      key={page}
                      className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => goToPage(page)}
                      title={`Go to page ${page}`}
                      disabled={loading}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={goToNextPage}
                    disabled={currentPageNum === totalPages || loading}
                    style={{
                      opacity: currentPageNum === totalPages ? 0.5 : 1,
                      cursor: currentPageNum === totalPages ? 'not-allowed' : 'pointer'
                    }}
                    title="Next page"
                  >
                    Next →
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <div className="card-title"><Calendar size={16} style={{ verticalAlign: '-3px' }} /> Upcoming Birthdays</div>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => openExportModal('upcoming')}
              title="Export upcoming birthdays"
              disabled={isSubmitting}
            >
              <Download size={14} />
            </button>
          </div>
          {loading && upcoming.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ color: 'var(--text-light)' }}>Loading...</div>
            </div>
          ) : upcoming.length > 0 ? (
            upcoming.map(m => (
              <div key={m.id} className="birthday-card">
                <div className="bday-avatar" style={{ background: m.color || '#0891b2' }}>{getInitials(m.name)}</div>
                <div style={{ flex: 1 }}>
                  <div className="bday-name">{m.name}</div>
                  <div className="bday-date"><Calendar size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{formatDate(m.dob)} · {m.group || 'General'}</div>
                </div>
                <div>
                  <span className={`badge ${m.days === 0 ? 'badge-green' : m.days === '-' ? 'badge-red' : 'badge-amber'}`}>
                    {m.days === 0 ? <><PartyPopper size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Today!</> : m.days === '-' ? 'Passed' : m.days + ' days'}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><PartyPopper size={32} strokeWidth={1.5} /></div>
              <div style={{ color: 'var(--text-light)' }}>No upcoming birthdays</div>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-title" style={{ marginBottom: '12px' }}><BarChart3 size={16} style={{ verticalAlign: '-3px' }} /> Birthday Stats</div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ color: 'var(--text-light)' }}>Loading stats...</div>
            </div>
          ) : (
            <>
              {[
                ['Messages Sent This Year', statsData.messages_sent_this_year || 0, Send],
                ['Avg Response Rate', `${statsData.stats?.avg_response_rate || 0}%`, TrendingUp],
                ['Members with Birthday Data', statsData.with_birthday || 0, CheckCircle2],
                ['Missing Birthdays', statsData.without_birthday || 0, AlertTriangle]
              ].map(([label, value, Icon]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-light)' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-mid)' }}><Icon size={13} style={{ verticalAlign: '-2px', marginRight: '4px' }} />{label}</span>
                  <span style={{ fontSize: '13px', fontWeight: '700', fontFamily: 'JetBrains Mono, monospace', color: 'var(--navy)' }}>
                    {typeof value === 'number' ? value.toLocaleString() : value}
                  </span>
                </div>
              ))}
              <div style={{ marginTop: '14px' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '8px' }}>Birthdays by month</div>
                <div className="bar-chart" style={{ height: '60px' }}>
                  {statsData.birthdays_by_month && Object.keys(statsData.birthdays_by_month).length > 0 ? (
                    Object.entries(statsData.birthdays_by_month).map(([month, count]) => {
                      const maxVal = Math.max(...Object.values(statsData.birthdays_by_month), 1);
                      return (
                        <div key={month} className="bar-col">
                          <div className="bar" style={{
                            height: `${(count / maxVal) * 50}px`,
                            background: parseInt(month) === new Date().getMonth() + 1 ? 'var(--gold)' : 'var(--navy)',
                            opacity: parseInt(month) === new Date().getMonth() + 1 ? 1 : 0.4
                          }}></div>
                          <div className="bar-label">{['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'][parseInt(month) - 1]}</div>
                        </div>
                      );
                    })
                  ) : (
                    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0].map((v, i) => (
                      <div key={i} className="bar-col">
                        <div className="bar" style={{ height: '0px', background: 'var(--navy)', opacity: 0.4 }}></div>
                        <div className="bar-label">{['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'][i]}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 1024px) {
          .grid-65-35 {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .search-box {
            max-width: 100% !important;
          }
          .birthday-card {
            flex-wrap: wrap;
          }
        }
        @media (max-width: 480px) {
          .table-wrap {
            overflow-x: auto;
          }
          .table-wrap table {
            min-width: 500px;
          }
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .toggle {
          position: relative;
          display: inline-block;
          width: 40px;
          height: 22px;
          flex-shrink: 0;
        }
        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #ccc;
          transition: 0.3s;
          border-radius: 22px;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 3px;
          bottom: 3px;
          background: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        .toggle input:checked + .toggle-slider {
          background: var(--gold);
        }
        .toggle input:checked + .toggle-slider:before {
          transform: translateX(18px);
        }
        .birthday-card {
          display: flex;
          align-items: center;
          padding: 10px 12px;
          margin-bottom: 8px;
          background: var(--cream);
          border-radius: 8px;
          gap: 12px;
          transition: all 0.2s;
        }
        .birthday-card:hover {
          background: var(--gold-pale);
        }
        .bday-avatar {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 14px;
          flex-shrink: 0;
        }
        .bday-name {
          font-size: 13px;
          font-weight: 500;
          color: var(--navy);
        }
        .bday-date {
          font-size: 11px;
          color: var(--text-light);
        }
        .bar-chart {
          display: flex;
          align-items: flex-end;
          gap: 4px;
          height: 60px;
        }
        .bar-col {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          height: 100%;
        }
        .bar {
          width: 100%;
          border-radius: 3px 3px 0 0;
          transition: height 0.3s ease;
          min-height: 2px;
        }
        .bar-label {
          font-size: 8px;
          color: var(--text-light);
          margin-top: 3px;
        }
        .member-row-avatar {
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          flex-shrink: 0;
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-red {
          background: var(--red-bg);
          color: var(--red);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
      `}</style>
    </div>
  );
};

export default Birthdays;